import { getElloConditionTitle } from './ConditionTitle'

export const LEGACY_CONDITION_REQUIREMENT_TITLES = {
  EARNEST_MONEY_VERIFICATION:
    'Upload your earnest money deposit check and receipt',
  GIFT_LETTER: 'Upload your signed gift letter',
  HOMEOWNERS_INSURANCE: 'Upload your HOI policy',
  MOST_RECENT_MORTGAGE_STATEMENT:
    'Last mortgage statement for ALL properties owned',
  PAY_STUBS_30_DAYS: 'Upload 30 days of pay stubs',
  SIGNED_PURCHASE_AGREEMENT: 'Purchase Contract + Amendments',
} as const

// Earlier legacy titles still on existing conditions; they map to the same
// requirement so reconciliation and the insert duplicate check still see them.
const RETIRED_LEGACY_CONDITION_TITLES: Partial<
  Record<LegacyConditionRequirementKey, string[]>
> = {
  SIGNED_PURCHASE_AGREEMENT: ['Upload your purchase contract'],
}

// Shared by the legacy and non-AUS purchase contract conditions.
export const PURCHASE_CONTRACT_DESCRIPTION =
  'Please upload your signed purchase contract, including all pages and any updates or addendums. ' +
  'Make sure the version provided reflects the final agreed terms and is signed by all parties.'

export type LegacyConditionRequirementKey =
  keyof typeof LEGACY_CONDITION_REQUIREMENT_TITLES

const AUS_LEGACY_REQUIREMENT_KEYS = {
  'assets earnest money verification': 'EARNEST_MONEY_VERIFICATION',
  'assets gift letter': 'GIFT_LETTER',
  'homeowners insurance policy': 'HOMEOWNERS_INSURANCE',
  'income pay stubs covering a full 30 days': 'PAY_STUBS_30_DAYS',
  'mortgage statements all properties owned': 'MOST_RECENT_MORTGAGE_STATEMENT',
  'property purchase contract': 'SIGNED_PURCHASE_AGREEMENT',
  'purchase contract amendments': 'SIGNED_PURCHASE_AGREEMENT',
} as const satisfies Record<string, LegacyConditionRequirementKey>

function normalizeConditionRequirementTitle(title: string): string {
  return getElloConditionTitle({ title, isCoBorrower: true })
    .replace(/\b(?:19|20)\d{2}\b/g, '<year>')
    .replace(/&/g, ' and ')
    .toLowerCase()
    .replace(/[^a-z0-9<>]+/g, ' ')
    .trim()
}

// Each requirement's current legacy title first, then its retired titles.
const LEGACY_TITLES_BY_KEY = (
  Object.entries(LEGACY_CONDITION_REQUIREMENT_TITLES) as [
    LegacyConditionRequirementKey,
    string,
  ][]
).map(([key, title]) => ({
  key,
  titles: [title, ...(RETIRED_LEGACY_CONDITION_TITLES[key] ?? [])],
}))

const LEGACY_REQUIREMENT_KEYS_BY_TITLE = new Map(
  LEGACY_TITLES_BY_KEY.flatMap(({ key, titles }) =>
    titles.map((title) => [normalizeConditionRequirementTitle(title), key])
  ) as [string, LegacyConditionRequirementKey][]
)

/** A legacy title plus the retired titles of the same requirement. */
export function getLegacyConditionTitleAliases(title: string): string[] {
  return (
    LEGACY_TITLES_BY_KEY.find(({ titles }) => titles[0] === title)?.titles ?? [
      title,
    ]
  )
}

export function resolveLegacyRequirementKey(
  title: string | null | undefined
): LegacyConditionRequirementKey | null {
  if (!title) {
    return null
  }

  return (
    LEGACY_REQUIREMENT_KEYS_BY_TITLE.get(
      normalizeConditionRequirementTitle(title)
    ) ?? null
  )
}

// Matches both the legacy title and the titles auto conditioning creates for
// the same requirement (AUS and non-AUS resolvers).
export function resolveConditionRequirementKey(
  title: string | null | undefined
): LegacyConditionRequirementKey | null {
  if (!title) return null

  const normalizedTitle = normalizeConditionRequirementTitle(title)
  return (
    AUS_LEGACY_REQUIREMENT_KEYS[
      normalizedTitle as keyof typeof AUS_LEGACY_REQUIREMENT_KEYS
    ] ?? resolveLegacyRequirementKey(title)
  )
}

export function isPurchaseContractConditionTitle(
  title: string | null | undefined
): boolean {
  return resolveConditionRequirementKey(title) === 'SIGNED_PURCHASE_AGREEMENT'
}
